import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useInventory } from '../contexts/InventoryContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNotification } from '../contexts/NotificationContext';
import { supabase } from '../lib/supabase';
import { sendShipmentNotification } from '../lib/email';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { BarcodeScanner } from '../components/ui/BarcodeScanner';
import { ExternalScanner } from '../components/ui/ExternalScanner';
import { QuantityStepper } from '../components/ui/QuantityStepper';
import { ItemPicker } from '../components/inventory/ItemPicker';
import { PostNLLabelModal } from '../components/shipping/PostNLLabelModal';
import { detectCarrier, getCarrierColor, CarrierType } from '../utils/carrierDetection';
import { getAdminId } from '../utils/getAdminId';
import { resolveWedgeScan } from '../utils/resolveWedgeScan';
import { useOfflineGuard } from '../components/ui/OfflineBanner';
import { useProductAttributes } from '../hooks/use-product-attributes';
import {
  addToWarehouseLocation,
  ensureDefaultLocations,
  fetchLocationQuantitiesForProducts,
  resolveWarehouseLocation,
  LocationQuantity
} from '../utils/locationInventory';
import {
  Package,
  Truck,
  Plus,
  Trash2,
  Save,
  Printer,
  AlertTriangle,
  AlertCircle,
  Camera,
  Zap,
  X,
  ArrowDown,
  ChevronDown,
  ChevronUp,
  ScanLine,
  Edit3,
  WifiOff,
  History,
  Clock,
  MapPin,
  Mail,
  RotateCcw,
  Tag,
  Download,
  ExternalLink
} from 'lucide-react';

interface ShipmentItem {
  product_id: string;
  product_name: string;
  quantity: number;
  current_stock: number;
  remaining_stock: number;
  category?: string;
  location?: string;
  sku?: string;
  reference_code?: string;
  has_error: boolean;
  is_low_stock: boolean;
  color_id?: string;
  size_id?: string;
  animationKey?: string;
}

type AddItemTab = 'scan' | 'manual';

interface ScannedItemData {
  barcode: string;
  existingItem?: any;
  isAvailable: boolean;
}

interface SavedShipment {
  id: string;
  shipment_number: string;
  customer_name: string;
  customer_email?: string;
  shipping_address: string;
  tracking_code?: string;
  carrier?: string;
  status: string;
  notes?: string;
  shipping_date: string;
  created_at: string;
  items: SavedShipmentItem[];
  label_url?: string;
  label_generated_at?: string;
  barcode_number?: string;
  weight_grams?: number;
  service_type?: string;
}

interface SavedShipmentItem {
  id: string;
  product_id: string;
  product_name: string;
  quantity: number;
  color_id?: string;
  size_id?: string;
  weight?: number;
  weight_unit?: string;
}

export const OutgoingShipments: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { allItems, allItemsLoading, fetchAllItems, refreshInventory, invalidateAllItemsCache } = useInventory();
  const { success, error: showError, info, warning, showUndoNotification } = useNotification();
  const { isNetworkOffline } = useOfflineGuard();
  const [loading, setLoading] = useState(false);
  const [shipmentItems, setShipmentItems] = useState<ShipmentItem[]>([]);
  const [companyName, setCompanyName] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [trackingCode, setTrackingCode] = useState('');
  const [notes, setNotes] = useState('');
  const [showCameraScanner, setShowCameraScanner] = useState(false);
  const [showExternalScanner, setShowExternalScanner] = useState(false);
  const [showScannedItemModal, setShowScannedItemModal] = useState(false);
  const [scannedData, setScannedData] = useState<ScannedItemData | null>(null);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [quantity, setQuantity] = useState<number | ''>('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showShipmentInfo, setShowShipmentInfo] = useState(true);
  const [activeTab, setActiveTab] = useState<AddItemTab>(() => {
    const saved = localStorage.getItem('outgoing-shipments-tab');
    return (saved === 'scan' || saved === 'manual') ? saved : 'manual';
  });
  const [manualBarcode, setManualBarcode] = useState('');
  const [lastAddedKey, setLastAddedKey] = useState<string | null>(null);
  const [removingIndex, setRemovingIndex] = useState<number | null>(null);
  const [itemCountKey, setItemCountKey] = useState(0);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [savedShipments, setSavedShipments] = useState<SavedShipment[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [expandedShipmentId, setExpandedShipmentId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [deletingShipmentId, setDeletingShipmentId] = useState<string | null>(null);
  const [labelModalShipment, setLabelModalShipment] = useState<SavedShipment | null>(null);
  const [availableLocations, setAvailableLocations] = useState<{ id: string; name: string }[]>([]);
  const [productLocationQtys, setProductLocationQtys] = useState<Record<string, LocationQuantity[]>>({});

  const quantityInputRef = useRef<HTMLInputElement>(null);

  const canModify = user?.role !== 'viewer';
  const adminId = getAdminId(user);

  const { colors, sizes } = useProductAttributes(adminId);

  const warehouseLocation = useMemo(
    () => availableLocations.find(l => l.name.toLowerCase().includes('warehouse')),
    [availableLocations]
  );

  const loadAllProductLocationQtys = useCallback(async () => {
    if (!adminId || allItems.length === 0) return;
    const productIds = allItems.map(item => item.id);
    const qtysMap = await fetchLocationQuantitiesForProducts(productIds, adminId);
    const result: Record<string, LocationQuantity[]> = {};
    qtysMap.forEach((qtys, productId) => {
      result[productId] = qtys;
    });
    setProductLocationQtys(result);
  }, [adminId, allItems]);

  useEffect(() => {
    if (adminId) {
      ensureDefaultLocations(adminId).then(setAvailableLocations);
    }
  }, [adminId]);

  useEffect(() => {
    if (adminId && allItems.length > 0 && availableLocations.length > 0) {
      loadAllProductLocationQtys();
    }
  }, [adminId, allItems.length, availableLocations.length, loadAllProductLocationQtys]);

  const getWarehouseQuantity = useCallback(
    (productId: string): number => {
      if (!warehouseLocation) return 0;
      const locQtys = productLocationQtys[productId];
      if (!locQtys) return 0;
      const wh = locQtys.find(q => q.locationId === warehouseLocation.id);
      return wh?.quantity ?? 0;
    },
    [warehouseLocation, productLocationQtys]
  );

  useEffect(() => {
    if (adminId) {
      fetchShipmentHistory();
      fetchAllItems();
    }
  }, [adminId, fetchAllItems]);

  useEffect(() => {
    localStorage.setItem('outgoing-shipments-tab', activeTab);
  }, [activeTab]);

  const fetchShipmentHistory = async () => {
    if (!adminId) return;
    setLoadingHistory(true);
    try {
      const { data: shipments, error } = await supabase
        .from('outgoing_shipments')
        .select('*')
        .eq('admin_id', adminId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      const shipmentsWithItems: SavedShipment[] = [];
      for (const shipment of shipments || []) {
        const { data: items } = await supabase
          .from('outgoing_shipment_items')
          .select(`
            id,
            product_id,
            quantity,
            products (
              name,
              color_id,
              size_id,
              weight,
              weight_unit
            )
          `)
          .eq('shipment_id', shipment.id);

        shipmentsWithItems.push({
          ...shipment,
          items: (items || []).map((item: any) => ({
            id: item.id,
            product_id: item.product_id,
            product_name: item.products?.name || 'Unknown Product',
            quantity: item.quantity,
            color_id: item.products?.color_id,
            size_id: item.products?.size_id,
            weight: item.products?.weight,
            weight_unit: item.products?.weight_unit
          }))
        });
      }

      setSavedShipments(shipmentsWithItems);
    } catch (err) {
      console.error('Error fetching shipment history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleDeleteShipment = async (shipmentId: string) => {
    if (isNetworkOffline) {
      showError('Cannot delete shipment while offline');
      return;
    }

    setDeletingShipmentId(shipmentId);
    try {
      const shipmentToDelete = savedShipments.find(s => s.id === shipmentId);
      if (!shipmentToDelete) {
        showError('Shipment not found');
        return;
      }

      for (const item of shipmentToDelete.items) {
        const { data: currentProduct, error: fetchError } = await supabase
          .from('products')
          .select('quantity')
          .eq('id', item.product_id)
          .maybeSingle();

        if (fetchError) throw fetchError;

        if (currentProduct) {
          await addToWarehouseLocation(item.product_id, adminId, item.quantity);
        }
      }

      const { error: deleteItemsError } = await supabase
        .from('outgoing_shipment_items')
        .delete()
        .eq('shipment_id', shipmentId);

      if (deleteItemsError) throw deleteItemsError;

      const { error: deleteShipmentError } = await supabase
        .from('outgoing_shipments')
        .delete()
        .eq('id', shipmentId);

      if (deleteShipmentError) throw deleteShipmentError;

      await supabase.from('activity_logs').insert({
        admin_id: adminId,
        user_id: user!.id,
        action_type: 'delete_outgoing_shipment',
        entity_type: 'outgoing_shipment',
        entity_id: shipmentId,
        description: `${user!.full_name || user!.email} deleted outgoing shipment ${shipmentToDelete.shipment_number} and restored ${shipmentToDelete.items.reduce((sum, i) => sum + i.quantity, 0)} units to inventory`,
        metadata: {
          shipment_number: shipmentToDelete.shipment_number,
          customer_name: shipmentToDelete.customer_name,
          items_restored: shipmentToDelete.items.map(i => ({
            product_id: i.product_id,
            product_name: i.product_name,
            quantity: i.quantity
          }))
        },
        created_at: new Date().toISOString()
      });

      success(`Shipment deleted and ${shipmentToDelete.items.reduce((sum, i) => sum + i.quantity, 0)} units restored to inventory`);
      setShowDeleteConfirm(null);
      await fetchShipmentHistory();
      invalidateAllItemsCache();
      await fetchAllItems(true);
      await loadAllProductLocationQtys();
    } catch (err) {
      console.error('Error deleting shipment:', err);
      showError('Failed to delete shipment');
    } finally {
      setDeletingShipmentId(null);
    }
  };

  const getColorName = (colorId?: string) => colors.find(c => c.id === colorId)?.name || '';
  const getColorHex = (colorId?: string) => colors.find(c => c.id === colorId)?.hex_code || '';
  const getSizeName = (sizeId?: string) => sizes.find(s => s.id === sizeId)?.name || '';

  const handleLabelGenerated = async () => {
    await fetchShipmentHistory();
    success('PostNL shipping label generated successfully');
  };

  const selectedProductIds = new Set(shipmentItems.map(si => si.product_id).filter(Boolean));
  const availableItems = allItems.filter(item => getWarehouseQuantity(item.id) > 0);

  const allCategories = useMemo(() => {
    const cats = new Set<string>();
    allItems.forEach(item => {
      if (item.category) cats.add(item.category);
    });
    return Array.from(cats).sort();
  }, [allItems]);

  const handleScanResult = async (code: string) => {
    const existingItem = allItems.find(
      item => item.barcode === code || item.sku === code || item.batch === code
    );

    const isAvailable = existingItem && getWarehouseQuantity(existingItem.id) > 0;

    setScannedData({
      barcode: code,
      existingItem: existingItem || undefined,
      isAvailable: !!isAvailable
    });

    setShowCameraScanner(false);
    setShowExternalScanner(false);
    setShowScannedItemModal(true);

    if (isAvailable) {
      setSelectedProduct(existingItem.id);
      setQuantity('');
      info(`Found: ${existingItem.name}`);
    } else if (existingItem) {
      setSelectedProduct('');
      setQuantity('');
      warning('Item is out of stock');
    } else {
      setSelectedProduct('');
      setQuantity('');
      warning('Item not found in inventory');
    }
  };

  const handleManualBarcodeSubmit = async () => {
    const raw = manualBarcode.trim();
    if (!raw) return;

    const resolved = await resolveWedgeScan(adminId, raw);
    setManualBarcode('');

    if (resolved.kind === 'unknown' || resolved.kind === 'epc_error') {
      warning(resolved.message);
      return;
    }

    if (resolved.kind === 'epc') {
      if (resolved.integrityAlarm) {
        warning(resolved.message || 'DATA INTEGRITY ALARM — unit record and EPC disagree');
        return;
      }
      if (!resolved.unitFound) {
        warning(resolved.message || 'No unit record for this EPC. Using variant from EPC base.');
      }
      await handleScanResult(resolved.value);
      return;
    }

    await handleScanResult(resolved.value);
  };

  const handleAddScannedItem = () => {
    addItemToShipment();
    setShowScannedItemModal(false);
    setScannedData(null);
  };

  const handleCancelScannedItem = () => {
    setShowScannedItemModal(false);
    setScannedData(null);
    setSelectedProduct('');
    setQuantity('');
  };

  const addItemToShipment = () => {
    if (!selectedProduct) {
      showError('Please select an item');
      return;
    }

    if (!quantity || quantity <= 0) {
      showError('Please enter a valid quantity');
      return;
    }

    const qty = Number(quantity);

    const product = allItems.find(p => p.id === selectedProduct);
    if (!product) {
      showError('Product not found in inventory');
      return;
    }

    const whQty = getWarehouseQuantity(product.id);
    const remaining = whQty - qty;
    const hasError = qty > whQty;
    const isLowStock = remaining <= 10 && remaining > 0;

    if (shipmentItems.find(item => item.product_id === product.id)) {
      showError('Item already added to shipment');
      return;
    }

    const animationKey = `item-${Date.now()}`;

    const itemData: ShipmentItem = {
      product_id: product.id,
      product_name: product.name,
      quantity: qty,
      current_stock: whQty,
      remaining_stock: remaining,
      category: product.category,
      location: product.location,
      sku: product.sku,
      has_error: hasError,
      is_low_stock: isLowStock,
      color_id: product.color_id,
      size_id: product.size_id,
      animationKey
    };

    setShipmentItems([...shipmentItems, itemData]);
    setLastAddedKey(animationKey);
    setItemCountKey(prev => prev + 1);

    success(`Added ${itemData.product_name} (${itemData.quantity} units)`);

    setSelectedProduct('');
    setQuantity('');

    setTimeout(() => setLastAddedKey(null), 500);
  };

  const removeItem = useCallback((index: number) => {
    const removedItem = shipmentItems[index];
    setRemovingIndex(index);

    setTimeout(() => {
      setShipmentItems(prev => prev.filter((_, i) => i !== index));
      setRemovingIndex(null);
      setItemCountKey(prev => prev + 1);

      showUndoNotification(
        `Removed "${removedItem.product_name}"`,
        () => {
          setShipmentItems(prev => {
            const newItems = [...prev];
            newItems.splice(index, 0, { ...removedItem, animationKey: `undo-${Date.now()}` });
            return newItems;
          });
          success('Item restored');
        }
      );
    }, 300);
  }, [shipmentItems, showUndoNotification, success]);

  const updateItemQuantity = (index: number, newQty: number) => {
    const qty = newQty || 0;
    const updatedItems = [...shipmentItems];
    const item = updatedItems[index];

    const remaining = item.current_stock - qty;
    item.quantity = qty;
    item.remaining_stock = remaining;
    item.has_error = qty > item.current_stock;
    item.is_low_stock = remaining <= 10 && remaining > 0;

    setShipmentItems(updatedItems);
  };

  const handleClearAll = () => {
    setShipmentItems([]);
    setCompanyName('');
    setShippingAddress('');
    setCompanyEmail('');
    setTrackingCode('');
    setNotes('');
    setValidationErrors([]);
    setShowClearConfirm(false);
    info('Shipment cleared');
  };

  const selectedProductData = selectedProduct
    ? allItems.find(p => p.id === selectedProduct)
    : null;

  const validateShipment = (): boolean => {
    const errors: string[] = [];

    if (!companyName.trim()) {
      errors.push('Company name is required');
    }

    if (shipmentItems.length === 0) {
      errors.push('At least one item must be added');
    }

    // If tracking code is provided, shipping address is required
    if (trackingCode.trim() && !shippingAddress.trim()) {
      errors.push('Shipping address is required when tracking code is provided');
    }

    // Validate shipping address has zip code if provided
    if (trackingCode.trim() && shippingAddress.trim()) {
      // Check if address contains what looks like a zip/postal code
      const hasZipCode = /\b\d{4,5}(-\d{4})?\b|\b[A-Z]\d[A-Z]\s?\d[A-Z]\d\b|\b\d{4}\s?[A-Z]{2}\b/i.test(shippingAddress);
      if (!hasZipCode) {
        errors.push('Shipping address must include a valid zip/postal code for tracking');
      }
    }

    const invalidItems = shipmentItems.filter(item => item.has_error);
    if (invalidItems.length > 0) {
      errors.push(`${invalidItems.length} item(s) exceed available stock`);
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSaveShipment = async () => {
    if (isNetworkOffline) {
      showError('Cannot save shipment while offline');
      return;
    }

    if (!canModify) {
      alert(t('common.failed'));
      return;
    }

    if (!validateShipment()) {
      const errorMessage = validationErrors.join('\n');
      alert(`Please fix the following issues:\n\n${errorMessage}`);
      return;
    }

    try {
      setLoading(true);
      console.log('Saving outgoing shipment...');

      const adminId = getAdminId(user);
      const shipmentNumber = `OUT-${Date.now()}`;

      const carrierInfo = trackingCode ? detectCarrier(trackingCode) : null;

      const warehouse = await resolveWarehouseLocation(adminId);
      if (!warehouse) {
        alert('No warehouse location is configured. Please create a "Warehouse" location first.');
        return;
      }

      // Atomic: validate stock (fail closed), deduct, and insert the shipment +
      // line items in one transaction. Prevents partial writes and overselling.
      const { data: shipResult, error: shipmentError } = await supabase.rpc('ship_outgoing_atomic', {
        p_admin_id: adminId,
        p_user_id: user!.id,
        p_shipment_number: shipmentNumber,
        p_customer_name: companyName,
        p_customer_email: companyEmail.trim() || null,
        p_shipping_address: shippingAddress,
        p_tracking_code: trackingCode || null,
        p_carrier: carrierInfo?.carrier || null,
        p_tracking_status: trackingCode ? 'pending_pickup' : null,
        p_notes: notes,
        p_warehouse_location_id: warehouse.id,
        p_items: shipmentItems.map(item => ({ product_id: item.product_id, quantity: item.quantity }))
      });

      if (shipmentError) throw shipmentError;

      if (!shipResult?.success) {
        if (shipResult?.error === 'insufficient_stock') {
          const shortfalls = Array.isArray(shipResult.insufficient) ? shipResult.insufficient : [];
          const names = shortfalls
            .map((s: { product_id: string; requested: number; available: number }) => {
              const match = shipmentItems.find(i => i.product_id === s.product_id);
              return `${match?.product_name || s.product_id} (need ${s.requested}, have ${s.available})`;
            })
            .join('\n');
          alert(`Cannot ship — insufficient warehouse stock:\n\n${names}`);
        } else {
          alert(t('common.failed'));
        }
        return;
      }

      console.log('✅ Outgoing shipment saved successfully');

      const shipment = { id: shipResult.shipment_id as string };
      const totalQty = shipmentItems.reduce((sum, item) => sum + item.quantity, 0);

      if (companyEmail.trim()) {
        try {
          console.log('📧 Sending shipment notification email...');
          await sendShipmentNotification(
            {
              shipmentNumber: shipmentNumber,
              customerName: companyName,
              shippingAddress: shippingAddress,
              trackingCode: trackingCode || undefined,
              carrier: carrierInfo?.name || undefined,
              items: shipmentItems.map(item => ({
                product_name: item.product_name,
                quantity: item.quantity
              })),
              notes: notes || undefined
            },
            companyEmail.trim(),
            {
              company_name: user?.company_name || user?.full_name || 'Inventory Management',
              company_address: user?.company_address,
              company_email: user?.company_email || user?.email,
              company_phone: user?.company_phone,
              full_name: user?.full_name
            }
          );

          await supabase
            .from('outgoing_shipments')
            .update({ email_sent_at: new Date().toISOString() })
            .eq('id', shipment.id);

          console.log('✅ Shipment notification email sent successfully');
          alert(`✅ Outgoing shipment ${shipmentNumber} saved!\n\n${shipmentItems.length} items dispatched.\nInventory updated.\n📧 Email notification sent to ${companyEmail}`);
        } catch (emailError) {
          console.warn('⚠️ Failed to send email notification:', emailError);
          alert(`✅ Outgoing shipment ${shipmentNumber} saved!\n\n${shipmentItems.length} items dispatched.\nInventory updated.\n\n⚠️ Email notification failed to send.`);
        }
      } else {
        alert(`✅ Outgoing shipment ${shipmentNumber} saved!\n\n${shipmentItems.length} items dispatched.\nInventory updated.`);
      }

      setShipmentItems([]);
      setCompanyName('');
      setShippingAddress('');
      setCompanyEmail('');
      setTrackingCode('');
      setNotes('');
      setValidationErrors([]);
      invalidateAllItemsCache();
      await fetchAllItems(true);
      await loadAllProductLocationQtys();
      await fetchShipmentHistory();
    } catch (err) {
      console.error('❌ Error saving shipment:', err);
      alert(t('common.failed'));
    } finally {
      setLoading(false);
    }
  };

  if (!canModify) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
          <div className="flex items-center">
            <AlertTriangle className="h-6 w-6 text-yellow-600 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-yellow-800 dark:text-yellow-300">
                {t('common.viewOnly')}
              </h3>
              <p className="text-yellow-600 dark:text-yellow-400 mt-1">
                {t('common.viewerAccess')}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 pb-32 lg:pb-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('shipments.outgoing.title')}
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            Dispatch inventory and decrease stock levels
          </p>
        </div>
        <Button variant="secondary" onClick={() => window.print()} className="hidden sm:flex">
          <Printer className="h-4 w-4 mr-2" />
          {t('action.print')}
        </Button>
      </div>

      {validationErrors.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-red-800 dark:text-red-300">
                Validation Errors
              </h3>
              <ul className="mt-2 text-sm text-red-700 dark:text-red-400 list-disc list-inside">
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="card overflow-hidden">
        <button
          onClick={() => setShowShipmentInfo(!showShipmentInfo)}
          className="w-full p-4 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Shipment Information
            </h2>
            {!showShipmentInfo && companyName && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {companyName}
              </span>
            )}
          </div>
          {showShipmentInfo ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </button>

        <div className={`transition-all duration-300 overflow-hidden ${showShipmentInfo ? 'max-h-[500px]' : 'max-h-0'}`}>
          <div className="p-4 pt-2 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Company Name *"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Enter company name"
                required
              />
              <div>
                <Input
                  label={`Shipping Address${trackingCode ? ' *' : ''}`}
                  value={shippingAddress}
                  onChange={(e) => setShippingAddress(e.target.value)}
                  placeholder={trackingCode ? "Required for tracking (include zip code)" : "Optional shipping address"}
                  required={!!trackingCode}
                />
                {trackingCode && (
                  <p className="mt-1 text-xs text-blue-600 dark:text-blue-400">
                    Include zip/postal code for accurate package tracking
                  </p>
                )}
              </div>
              <div>
                <Input
                  label="Company Email (Optional)"
                  type="email"
                  value={companyEmail}
                  onChange={(e) => setCompanyEmail(e.target.value)}
                  placeholder="company@example.com"
                />
                {companyEmail && (
                  <p className="mt-1 text-xs text-green-600 dark:text-green-400">
                    Customer will receive shipment notification
                  </p>
                )}
              </div>
              <div>
                <Input
                  label="Tracking Code (Optional)"
                  value={trackingCode}
                  onChange={(e) => setTrackingCode(e.target.value)}
                  placeholder="Enter tracking code"
                />
                {trackingCode && (() => {
                  const carrierInfo = detectCarrier(trackingCode);
                  return (
                    <div className="mt-2 flex items-center gap-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getCarrierColor(carrierInfo.carrier)}`}>
                        {carrierInfo.name}
                      </span>
                      {carrierInfo.carrier === 'Unknown' && (
                        <span className="text-xs text-yellow-600 dark:text-yellow-400">
                          Carrier not recognized
                        </span>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
            <Input
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes"
            />
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <div className="flex">
            <button
              onClick={() => setActiveTab('scan')}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-all relative ${
                activeTab === 'scan'
                  ? 'text-teal-600 dark:text-teal-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <ScanLine className="h-4 w-4" />
                <span>Scan</span>
              </div>
              {activeTab === 'scan' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-500 dark:bg-teal-400" />
              )}
            </button>
            <button
              onClick={() => setActiveTab('manual')}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-all relative ${
                activeTab === 'manual'
                  ? 'text-teal-600 dark:text-teal-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Edit3 className="h-4 w-4" />
                <span>Manual</span>
              </div>
              {activeTab === 'manual' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-500 dark:bg-teal-400" />
              )}
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          <div className={`transition-opacity duration-200 ${activeTab === 'scan' ? 'block' : 'hidden'}`}>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button
                  variant="secondary"
                  onClick={() => setShowCameraScanner(true)}
                  className="min-h-[56px] flex items-center justify-center gap-3 bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/30"
                >
                  <Camera className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  <span className="font-medium text-blue-700 dark:text-blue-300">Camera Scanner</span>
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setShowExternalScanner(true)}
                  className="min-h-[56px] flex items-center justify-center gap-3 bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-800 hover:border-green-300 dark:hover:border-green-700 hover:bg-green-100 dark:hover:bg-green-900/30"
                >
                  <Zap className="h-5 w-5 text-green-600 dark:text-green-400" />
                  <span className="font-medium text-green-700 dark:text-green-300">External Scanner</span>
                </Button>
              </div>

              <div className="relative">
                <Input
                  label="Enter Barcode Manually"
                  value={manualBarcode}
                  onChange={(e) => setManualBarcode(e.target.value)}
                  placeholder="Type or paste barcode..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleManualBarcodeSubmit();
                    }
                  }}
                />
                {manualBarcode && (
                  <button
                    onClick={handleManualBarcodeSubmit}
                    className="absolute right-2 top-[34px] px-3 py-1.5 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors"
                  >
                    Go
                  </button>
                )}
              </div>

              <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Quick Tips</h4>
                <ul className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
                  <li>Use Camera Scanner for phone/tablet cameras</li>
                  <li>Use External Scanner for USB/Bluetooth devices</li>
                  <li>Or type the barcode directly above</li>
                </ul>
              </div>
            </div>
          </div>

          <div className={`transition-opacity duration-200 ${activeTab === 'manual' ? 'block' : 'hidden'}`}>
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1 lg:flex-[2]">
                {allItemsLoading ? (
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
                    <div className="animate-spin h-8 w-8 border-2 border-teal-500 border-t-transparent rounded-full mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">Loading inventory ({allItems.length} items)...</p>
                  </div>
                ) : (
                  <ItemPicker
                    items={availableItems}
                    colors={colors}
                    sizes={sizes}
                    allCategories={allCategories}
                    selectedItemId={selectedProduct || null}
                    excludedItemIds={selectedProductIds}
                    onSelect={(item) => {
                      setSelectedProduct(item.id);
                    }}
                    onClear={() => {
                      setSelectedProduct('');
                    }}
                    inlineMode={true}
                  />
                )}
              </div>

              <div className="lg:w-72 xl:w-80 flex-shrink-0">
                <div className="lg:sticky lg:top-4 space-y-3">
                  {selectedProductData ? (
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Package className="h-5 w-5 text-red-600 dark:text-red-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 dark:text-gray-100 truncate text-sm">
                            {selectedProductData.name}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Available: {getWarehouseQuantity(selectedProductData.id)} in stock
                          </div>
                        </div>
                      </div>
                      {(getColorName(selectedProductData.color_id) || getSizeName(selectedProductData.size_id)) && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {getColorName(selectedProductData.color_id) && (
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                              {getColorHex(selectedProductData.color_id) && (
                                <span
                                  className="w-2.5 h-2.5 rounded-full border border-amber-300 dark:border-amber-600"
                                  style={{ backgroundColor: getColorHex(selectedProductData.color_id) }}
                                />
                              )}
                              {getColorName(selectedProductData.color_id)}
                            </span>
                          )}
                          {getSizeName(selectedProductData.size_id) && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                              {getSizeName(selectedProductData.size_id)}
                            </span>
                          )}
                        </div>
                      )}
                      <div className="flex items-center justify-between text-sm p-2 bg-white dark:bg-gray-800 rounded-lg">
                        <span className="text-gray-600 dark:text-gray-400">After dispatch:</span>
                        <span className="font-bold text-red-600 dark:text-red-400">
                          {quantity ? `${getWarehouseQuantity(selectedProductData.id) - Number(quantity)} (-${quantity})` : '-'}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl text-center">
                      <Package className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">Select an item from the list</p>
                    </div>
                  )}

                  <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Quantity to Dispatch
                    </label>
                    <input
                      ref={quantityInputRef}
                      type="number"
                      value={quantity}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') {
                          setQuantity('');
                        } else {
                          const num = parseInt(val, 10);
                          if (!isNaN(num) && num >= 0) {
                            setQuantity(num);
                          }
                        }
                      }}
                      min={1}
                      max={selectedProductData ? getWarehouseQuantity(selectedProductData.id) : undefined}
                      className="w-full h-12 text-center text-xl font-bold bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:border-teal-500 dark:focus:border-teal-400 focus:ring-2 focus:ring-teal-500/20 transition-all text-gray-900 dark:text-gray-100"
                      placeholder="Enter quantity"
                    />
                  </div>

                  <Button
                    onClick={addItemToShipment}
                    className="w-full min-h-[48px] text-sm font-semibold"
                    disabled={!selectedProduct || !quantity || quantity <= 0}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add to Shipment
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {shipmentItems.length > 0 ? (
        <div className="card overflow-hidden">
          <div className="p-4 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Shipment Items
              </h3>
              <span
                key={itemCountKey}
                className="px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full text-sm font-bold animate-count-pop"
              >
                {shipmentItems.length}
              </span>
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Total: <span className="font-semibold text-red-600 dark:text-red-400">-{shipmentItems.reduce((sum, item) => sum + item.quantity, 0)}</span> units
            </div>
          </div>

          <div className="hidden lg:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    {t('inventory.table.name')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    {t('product.category')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Color / Size
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Current
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    <span className="text-red-600 dark:text-red-400">Dispatching</span>
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    <span className="text-red-600 dark:text-red-400">Remaining</span>
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    {t('inventory.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {shipmentItems.map((item, index) => {
                  const colorName = getColorName(item.color_id);
                  const colorHex = getColorHex(item.color_id);
                  const sizeName = getSizeName(item.size_id);
                  const isNewlyAdded = item.animationKey === lastAddedKey;
                  const isRemoving = removingIndex === index;
                  return (
                    <tr
                      key={item.animationKey || index}
                      className={`transition-all ${
                        isNewlyAdded ? 'animate-item-add' : ''
                      } ${isRemoving ? 'animate-item-remove' : ''} ${
                        item.has_error ? 'bg-red-50 dark:bg-red-900/10' : ''
                      }`}
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {item.product_name}
                          </div>
                          {item.sku && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">{item.sku}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {item.category ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                            {item.category}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex flex-wrap gap-1">
                          {colorName && (
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                              {colorHex && (
                                <span
                                  className="w-2.5 h-2.5 rounded-full border border-amber-300 dark:border-amber-600"
                                  style={{ backgroundColor: colorHex }}
                                />
                              )}
                              {colorName}
                            </span>
                          )}
                          {sizeName && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                              {sizeName}
                            </span>
                          )}
                          {!colorName && !sizeName && (
                            <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {item.current_stock}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex justify-center">
                          <QuantityStepper
                            value={item.quantity}
                            onChange={(val) => updateItemQuantity(index, val)}
                            min={1}
                            max={item.current_stock}
                            size="sm"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right">
                        <span className={`inline-flex items-center gap-1 text-sm font-bold ${
                          item.has_error ? 'text-red-600 dark:text-red-400' :
                          item.is_low_stock ? 'text-amber-600 dark:text-amber-400' :
                          'text-red-600 dark:text-red-400'
                        }`}>
                          {item.remaining_stock}
                          <ArrowDown className="h-3.5 w-3.5" />
                          {item.has_error && (
                            <span className="ml-1 text-xs">EXCEEDS!</span>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => removeItem(index)}
                          className="hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="lg:hidden divide-y divide-gray-200 dark:divide-gray-700">
            {shipmentItems.map((item, index) => {
              const colorName = getColorName(item.color_id);
              const colorHex = getColorHex(item.color_id);
              const sizeName = getSizeName(item.size_id);
              const isNewlyAdded = item.animationKey === lastAddedKey;
              const isRemoving = removingIndex === index;
              return (
                <div
                  key={item.animationKey || index}
                  className={`p-4 bg-white dark:bg-gray-900 ${
                    isNewlyAdded ? 'animate-item-add' : ''
                  } ${isRemoving ? 'animate-item-remove' : ''} ${
                    item.has_error ? 'bg-red-50 dark:bg-red-900/10' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                        {item.product_name}
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {item.category && (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                            {item.category}
                          </span>
                        )}
                        {colorName && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                            {colorHex && (
                              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: colorHex }} />
                            )}
                            {colorName}
                          </span>
                        )}
                        {sizeName && (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                            {sizeName}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => removeItem(index)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors touch-target"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="text-center">
                      <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                        {item.current_stock}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Current</div>
                    </div>
                    <div className="flex items-center">
                      <QuantityStepper
                        value={item.quantity}
                        onChange={(val) => updateItemQuantity(index, val)}
                        min={1}
                        max={item.current_stock}
                        size="sm"
                      />
                    </div>
                    <div className="text-center">
                      <div className={`text-lg font-bold flex items-center justify-center gap-0.5 ${
                        item.has_error ? 'text-red-600 dark:text-red-400' : 'text-red-600 dark:text-red-400'
                      }`}>
                        {item.remaining_stock}
                        <ArrowDown className="h-4 w-4" />
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Remaining</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="hidden lg:flex p-6 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 justify-end gap-4">
            <Button
              variant="secondary"
              onClick={() => setShowClearConfirm(true)}
            >
              Clear All
            </Button>
            <Button
              onClick={handleSaveShipment}
              disabled={loading || shipmentItems.some(i => i.has_error)}
              className="px-8"
            >
              <Save className="h-4 w-4 mr-2" />
              {loading ? t('message.loading') : t('action.save')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="card p-8 sm:p-12 text-center">
          <Truck className="h-12 sm:h-16 w-12 sm:w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            No items added yet
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Scan, select, or manually enter items to dispatch
          </p>
        </div>
      )}

      {shipmentItems.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 lg:hidden p-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-lg safe-bottom z-30">
          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={() => setShowClearConfirm(true)}
              className="flex-1 min-h-[52px]"
            >
              Clear
            </Button>
            <Button
              onClick={handleSaveShipment}
              disabled={loading || shipmentItems.some(i => i.has_error)}
              className="flex-[2] min-h-[52px]"
            >
              <Save className="h-5 w-5 mr-2" />
              {loading ? 'Saving...' : `Save (${shipmentItems.length} items)`}
            </Button>
          </div>
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="p-4 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <History className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Shipment History
            </h3>
            {savedShipments.length > 0 && (
              <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full text-sm">
                {savedShipments.length}
              </span>
            )}
          </div>
        </div>

        {loadingHistory ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mx-auto mb-3"></div>
            <p className="text-gray-500 dark:text-gray-400">Loading history...</p>
          </div>
        ) : savedShipments.length === 0 ? (
          <div className="p-8 text-center">
            <Truck className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">No shipments yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {savedShipments.map((shipment) => {
              const isExpanded = expandedShipmentId === shipment.id;
              const totalQuantity = shipment.items.reduce((sum, item) => sum + item.quantity, 0);
              const shipmentDate = new Date(shipment.shipping_date);

              return (
                <div key={shipment.id} className="bg-white dark:bg-gray-900">
                  <button
                    onClick={() => setExpandedShipmentId(isExpanded ? null : shipment.id)}
                    className="w-full p-4 flex items-center gap-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-left"
                  >
                    <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Truck className="h-5 w-5 text-red-600 dark:text-red-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                          {shipment.shipment_number}
                        </span>
                        {shipment.carrier && (
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getCarrierColor(shipment.carrier as CarrierType)}`}>
                            {shipment.carrier}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {shipment.customer_name}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm font-medium text-red-600 dark:text-red-400">
                        -{totalQuantity} units
                      </div>
                      <div className="text-xs text-gray-400 dark:text-gray-500 flex items-center justify-end gap-1">
                        <Clock className="h-3 w-3" />
                        {shipmentDate.toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      {isExpanded ? (
                        <ChevronUp className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="px-4 pb-4 space-y-3 animate-fade-in">
                      <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg space-y-2">
                        {shipment.shipping_address && (
                          <div className="flex items-start gap-2 text-sm">
                            <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600 dark:text-gray-400">{shipment.shipping_address}</span>
                          </div>
                        )}
                        {shipment.customer_email && (
                          <div className="flex items-center gap-2 text-sm">
                            <Mail className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <span className="text-gray-600 dark:text-gray-400">{shipment.customer_email}</span>
                          </div>
                        )}
                        {shipment.tracking_code && (
                          <div className="flex items-center gap-2 text-sm">
                            <Package className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <span className="text-gray-600 dark:text-gray-400 font-mono">{shipment.tracking_code}</span>
                          </div>
                        )}
                      </div>

                      <div className="space-y-2">
                        <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Items ({shipment.items.length})
                        </div>
                        <div className="space-y-1">
                          {shipment.items.map((item) => {
                            const colorName = getColorName(item.color_id);
                            const colorHex = getColorHex(item.color_id);
                            const sizeName = getSizeName(item.size_id);

                            return (
                              <div
                                key={item.id}
                                className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded-lg"
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="text-sm text-gray-900 dark:text-gray-100 truncate">
                                    {item.product_name}
                                  </span>
                                  {colorName && (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                                      {colorHex && (
                                        <span
                                          className="w-2 h-2 rounded-full"
                                          style={{ backgroundColor: colorHex }}
                                        />
                                      )}
                                      {colorName}
                                    </span>
                                  )}
                                  {sizeName && (
                                    <span className="px-1.5 py-0.5 rounded text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                      {sizeName}
                                    </span>
                                  )}
                                </div>
                                <span className="text-sm font-medium text-red-600 dark:text-red-400 flex-shrink-0">
                                  -{item.quantity}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {shipment.notes && (
                        <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                          <p className="text-sm text-amber-800 dark:text-amber-300">{shipment.notes}</p>
                        </div>
                      )}

                      {shipment.label_url && shipment.barcode_number && (
                        <div className="p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <Tag className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                            <span className="text-sm font-medium text-orange-800 dark:text-orange-300">
                              PostNL Label Generated
                            </span>
                          </div>
                          <p className="text-xs text-orange-700 dark:text-orange-400 font-mono mb-2">
                            {shipment.barcode_number}
                          </p>
                          <div className="flex gap-2">
                            <a
                              href={shipment.label_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 px-2 py-1 bg-orange-600 text-white text-xs font-medium rounded hover:bg-orange-700 transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Download className="h-3 w-3" />
                              Download
                            </a>
                            <a
                              href={`https://postnl.nl/tracktrace/?B=${encodeURIComponent(shipment.barcode_number)}&P=&D=&T=C`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 px-2 py-1 bg-white dark:bg-gray-800 text-orange-700 dark:text-orange-300 text-xs font-medium rounded border border-orange-300 dark:border-orange-700 hover:bg-orange-50 dark:hover:bg-orange-900/30 transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="h-3 w-3" />
                              Track
                            </a>
                          </div>
                        </div>
                      )}

                      <div className="flex flex-wrap justify-end gap-2 pt-2">
                        {shipment.shipping_address && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setLabelModalShipment(shipment);
                            }}
                            className="gap-2 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800 hover:bg-orange-100 dark:hover:bg-orange-900/40"
                          >
                            <Tag className="h-4 w-4" />
                            {shipment.label_url ? 'New Label' : 'Generate PostNL Label'}
                          </Button>
                        )}
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowDeleteConfirm(shipment.id);
                          }}
                          className="gap-2"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete & Restore Stock
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center px-4">
          <div className="relative w-full max-w-md p-6 shadow-2xl rounded-2xl bg-white dark:bg-gray-800 animate-scale-in">
            <div className="text-center">
              <div className="mx-auto w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
                <RotateCcw className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Delete Shipment & Restore Stock?
              </h3>
              {(() => {
                const shipment = savedShipments.find(s => s.id === showDeleteConfirm);
                if (!shipment) return null;
                const totalQty = shipment.items.reduce((sum, i) => sum + i.quantity, 0);
                return (
                  <div className="text-left space-y-3 mb-6">
                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                      This will delete shipment <strong>{shipment.shipment_number}</strong> and restore all items to inventory:
                    </p>
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                      <div className="text-sm text-green-800 dark:text-green-300 font-medium mb-2">
                        Stock to be restored: +{totalQty} units
                      </div>
                      <ul className="text-xs text-green-700 dark:text-green-400 space-y-1">
                        {shipment.items.map(item => (
                          <li key={item.id}>+{item.quantity} {item.product_name}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })()}
              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1 min-h-[48px]"
                  disabled={deletingShipmentId !== null}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={() => handleDeleteShipment(showDeleteConfirm)}
                  className="flex-1 min-h-[48px]"
                  disabled={deletingShipmentId !== null}
                >
                  {deletingShipmentId ? 'Deleting...' : 'Delete & Restore'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Camera Scanner */}
      <BarcodeScanner
        isOpen={showCameraScanner}
        onClose={() => setShowCameraScanner(false)}
        onScan={handleScanResult}
        title="Scan Barcode - Camera"
      />

      {/* External Scanner */}
      <ExternalScanner
        isOpen={showExternalScanner}
        onClose={() => setShowExternalScanner(false)}
        onScan={handleScanResult}
        mode="outgoing"
        title="Scan Barcode - External Device"
      />

      {showScannedItemModal && scannedData && (
        <div className="fixed inset-0 bg-gray-600/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-start justify-center pt-10 sm:pt-20 px-4">
          <div className="relative w-full max-w-2xl p-4 sm:p-6 shadow-2xl rounded-2xl bg-white dark:bg-gray-800 animate-scale-in">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {scannedData.isAvailable ? 'Item Found' : 'Item Not Available'}
                </h3>
                <button
                  onClick={handleCancelScannedItem}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className={`p-4 rounded-xl border ${
                scannedData.isAvailable
                  ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                  : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
              }`}>
                <div className="flex items-start gap-3">
                  <Package className={`h-5 w-5 mt-0.5 ${
                    scannedData.isAvailable
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-red-600 dark:text-red-400'
                  }`} />
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${
                      scannedData.isAvailable
                        ? 'text-blue-900 dark:text-blue-300'
                        : 'text-red-900 dark:text-red-300'
                    }`}>
                      Barcode: {scannedData.barcode}
                    </p>
                    {scannedData.existingItem ? (
                      <p className={`text-sm mt-1 ${
                        scannedData.isAvailable
                          ? 'text-blue-700 dark:text-blue-400'
                          : 'text-red-700 dark:text-red-400'
                      }`}>
                        {scannedData.isAvailable
                          ? `Available: ${getWarehouseQuantity(scannedData.existingItem.id)} ${scannedData.existingItem.unit || 'units'}`
                          : 'This item is out of stock'
                        }
                      </p>
                    ) : (
                      <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                        Item not found in inventory
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {scannedData.isAvailable ? (
                <>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                    <div className="text-sm space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Item Name:</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {scannedData.existingItem.name}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Category:</span>
                        <span className="text-gray-900 dark:text-gray-100">
                          {scannedData.existingItem.category || '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Location:</span>
                        <span className="text-gray-900 dark:text-gray-100">
                          {scannedData.existingItem.location || '-'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Quantity to Dispatch
                    </label>
                    <input
                      type="number"
                      value={quantity}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') {
                          setQuantity('');
                        } else {
                          const num = parseInt(val, 10);
                          if (!isNaN(num) && num >= 0) {
                            setQuantity(num);
                          }
                        }
                      }}
                      min={1}
                      max={getWarehouseQuantity(scannedData.existingItem.id)}
                      className="w-full h-14 text-center text-2xl font-bold bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:border-teal-500 dark:focus:border-teal-400 focus:ring-2 focus:ring-teal-500/20 transition-all text-gray-900 dark:text-gray-100"
                      placeholder="Enter quantity"
                      autoFocus
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      variant="secondary"
                      onClick={handleCancelScannedItem}
                      className="flex-1 min-h-[48px]"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleAddScannedItem}
                      className="flex-1 min-h-[48px]"
                      disabled={!quantity || quantity <= 0 || quantity > getWarehouseQuantity(scannedData.existingItem.id)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add to Shipment
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex justify-center pt-4">
                  <Button
                    variant="secondary"
                    onClick={handleCancelScannedItem}
                    className="px-8 min-h-[48px]"
                  >
                    Close
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showClearConfirm && (
        <div className="fixed inset-0 bg-gray-600/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center px-4">
          <div className="relative w-full max-w-md p-6 shadow-2xl rounded-2xl bg-white dark:bg-gray-800 animate-scale-in">
            <div className="text-center">
              <div className="mx-auto w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Clear All Items?
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                This will remove all {shipmentItems.length} items from the current shipment. This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  onClick={() => setShowClearConfirm(false)}
                  className="flex-1 min-h-[48px]"
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={handleClearAll}
                  className="flex-1 min-h-[48px]"
                >
                  Clear All
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {labelModalShipment && (
        <PostNLLabelModal
          isOpen={!!labelModalShipment}
          onClose={() => setLabelModalShipment(null)}
          shipment={labelModalShipment}
          onLabelGenerated={handleLabelGenerated}
        />
      )}
    </div>
  );
};
